<script setup>
import { ref, computed, nextTick } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { Chrome } from '@lk77/vue3-color';

const props = defineProps({
  modelValue: {
    type: String,
    default: '#3B82F6',
  },
});

const emit = defineEmits(['update:modelValue']);

const open = ref(false);
const triggerRef = ref(null);
const panelRef = ref(null);
const panelStyle = ref({});
const mode = ref('basic');
const draftColor = ref('#3B82F6');

const presetColors = [
  '#94A3B8',
  '#64748B',
  '#475569',
  '#3B82F6',
  '#2563EB',
  '#1D4ED8',
  '#8B5CF6',
  '#7C3AED',
  '#6D28D9',
  '#F59E0B',
  '#D97706',
  '#B45309',
  '#10B981',
  '#059669',
  '#047857',
  '#EF4444',
  '#DC2626',
  '#B91C1C',
  '#EC4899',
  '#DB2777',
  '#BE185D',
  '#14B8A6',
  '#0D9488',
  '#0F766E',
];

const currentColor = computed(() => props.modelValue);

const selectBasicColor = color => {
  emit('update:modelValue', color);
  open.value = false;
};

const onAdvancedColorChange = payload => {
  if (!payload?.hex) return;
  draftColor.value = payload.hex.toUpperCase();
};

const openAdvanced = () => {
  mode.value = 'advanced';
  draftColor.value = currentColor.value;
};

const backToBasic = () => {
  mode.value = 'basic';
};

const saveAdvanced = () => {
  emit('update:modelValue', draftColor.value);
  open.value = false;
};

const togglePicker = () => {
  open.value = !open.value;
  if (!open.value) return;

  mode.value = 'basic';
  draftColor.value = currentColor.value;

  nextTick(() => {
    const rect = triggerRef.value?.getBoundingClientRect?.();
    if (!rect) return;

    panelStyle.value = {
      position: 'fixed',
      top: `${rect.bottom + 8}px`,
      left: `${Math.max(8, Math.min(rect.left, window.innerWidth - 320))}px`,
      zIndex: 9999,
    };
  });
};

onClickOutside(
  triggerRef,
  () => {
    if (!open.value) return;
    open.value = false;
  },
  { ignore: [panelRef] }
);
</script>

<template>
  <div class="relative" ref="triggerRef">
    <button
      type="button"
      class="w-10 h-10 rounded-lg border-2 border-n-weak hover:border-n-brand transition-colors flex items-center justify-center group"
      :style="{ backgroundColor: currentColor }"
      @click="togglePicker"
    >
      <span
        class="i-lucide-palette size-4 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow"
      />
    </button>

    <Teleport to="body">
      <div
        v-show="open"
        ref="panelRef"
        class="p-3 bg-n-solid-1 border border-n-strong rounded-lg shadow-xl w-72"
        :style="panelStyle"
      >
        <template v-if="mode === 'basic'">
          <div class="grid grid-cols-6 gap-2 mb-3">
            <button
              v-for="color in presetColors"
              :key="color"
              type="button"
              class="w-8 h-8 rounded-md border-2 transition-all hover:scale-110"
              :class="
                currentColor === color
                  ? 'border-n-brand ring-2 ring-n-brand ring-offset-2'
                  : 'border-n-weak'
              "
              :style="{ backgroundColor: color }"
              @click="selectBasicColor(color)"
            />
          </div>

          <button
            type="button"
            class="w-full py-1.5 text-xs font-medium border border-n-weak rounded-lg hover:border-n-brand transition-colors"
            @click="openAdvanced"
          >
            Cores avançadas
          </button>
        </template>

        <template v-else>
          <Chrome
            disable-alpha
            :model-value="draftColor"
            class="colorpicker--chrome"
            @update:model-value="onAdvancedColorChange"
          />

          <div class="flex items-center justify-between gap-2 mt-3">
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium border border-n-weak rounded-lg hover:border-n-brand transition-colors"
              @click="backToBasic"
            >
              Voltar
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium bg-n-brand text-white rounded-lg hover:bg-n-brand/90 transition-colors"
              @click="saveAdvanced"
            >
              Salvar
            </button>
          </div>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.colorpicker--chrome.vc-chrome {
  @apply shadow-none border border-n-weak rounded-lg bg-n-background w-full;

  :deep() {
    .vc-chrome-saturation-wrap {
      @apply rounded-t-lg;

      .vc-saturation {
        @apply rounded-t-lg;
      }
    }

    .vc-chrome-body {
      @apply rounded-b-lg bg-n-alpha-3;

      .vc-chrome-toggle-btn {
        .vc-chrome-toggle-icon svg {
          @apply [&>path]:fill-n-slate-10 left-3 relative;
        }

        .vc-chrome-toggle-icon-highlight {
          @apply bg-n-background;
        }
      }
    }

    input,
    .vc-input__input {
      @apply bg-n-background text-n-slate-12 rounded-md shadow-none;
    }

    .vc-input__label {
      @apply text-n-slate-11;
    }
  }
}
</style>

